import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbExternalLink, TbFolderSymlink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useListStore } from '@/stores';
import { List } from '@/types';

import { getWorkspaceResourceRemovalActionLabel, WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface ListsActionsDropdownProps {
    list: List;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

const ListsActionsDropdown: FC<ListsActionsDropdownProps> = ({ list, children, onOpenChange, open }): JSX.Element => {
    const [renameVisible, setRenameVisible] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const { lists, toggleListFavorite, duplicateList, toggleListLock, toggleListArchive } = useListStore();
    const access = useWorkspaceResourceAccess('lists', list?.id);
    const createAccess = useWorkspaceResourceCreateAccess('lists', lists?.length ?? 0);

    const handleToggleFavorite = async () => {
        toggleListFavorite(list?.id, !list?.favorited);
    };

    const handleToggleLock = async () => {
        toggleListLock(list?.id, !list?.locked);
    };

    const handleToggleArchive = async () => {
        toggleListArchive(list?.id, !list?.archived);
    };

    const handleDuplicateList = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicateList(list?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'favorited',
            icon: list?.favorited ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
            label: list?.favorited ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: handleToggleFavorite,
            permission: 'update',
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={list?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', list?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={list?.archived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', list?.archived),
            onClick: handleToggleArchive,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/lists/${list?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: list?.name,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: list?.name,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        {
            key: 'openInNewWindow',
            icon: <TbExternalLink size={'1rem'} />,
            label: 'Open in New Window',
            onClick: () => {
                window.open(`/lists/${list?.id}`, '_blank');
            },
            permission: 'read',
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/lists/${list?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`List ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.name,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'export',
        //     icon: <TbFileExport size='1rem' style={{ verticalAlign: '-0.175rem', transform: 'rotate(-90deg)' }} />,
        //     label: 'Export',
        //     children: [
        //         {
        //             key: 'exportXLSX',
        //             icon: <TbFileTypeXls size='1rem' />,
        //             label: 'Excel',
        //             onClick: () => {
        //                 setMoveToFolderVisible(true);
        //             },
        //         },
        //         {
        //             key: 'exportCSV',
        //             icon: <TbFileTypeCsv size='1rem' />,
        //             label: 'CSV',
        //             onClick: () => {
        //                 setMoveToFolderVisible(true);
        //             },
        //         },
        //     ],
        // },
        // {
        //     key: 'import',
        //     icon: <TbFileImport size='1rem' style={{ verticalAlign: '-0.175rem', transform: 'rotate(-90deg)' }} />,
        //     label: 'Import',
        //     children: [
        //         {
        //             key: 'importXLSX',
        //             icon: <TbFileTypeXls size='1rem' />,
        //             label: 'Excel',
        //             onClick: () => {
        //                 setMoveToFolderVisible(true);
        //             },
        //         },
        //         {
        //             key: 'importCSV',
        //             icon: <TbFileTypeCsv size='1rem' />,
        //             label: 'CSV',
        //             onClick: () => {
        //                 setMoveToFolderVisible(true);
        //             },
        //         },
        //     ],
        // },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateList,
            permission: 'read',
        },
        {
            key: 'moveToFolder',
            icon: <TbFolderSymlink size='1rem' />,
            label: 'Move to Folder',
            onClick: () => {
                setMoveToFolderVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: getWorkspaceResourceRemovalActionLabel('lists'),
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: list.id, resourceType: 'lists', title: list.name }}
                items={items}
                menuStyle={{ minWidth: 16 * 18 }}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='List'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={list}
                singularResource='list'
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={list}
                singularResource='list'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={list}
                singularResource='list'
            />
        </>
    );
};

export default ListsActionsDropdown;
